<template>
  <div class="modal-overlay">
    <div class="modal-card">

      <div class="icon-box danger-bg border-round-lg flex align-items-center justify-content-center mb-4">
        <div class="border-circle flex align-items-center justify-content-center bg-red-500"
          style="width: 1.5rem; height: 1.5rem;">
          <i class="pi pi-times text-white" style="font-size: 0.8rem; font-weight: bold;"></i>
        </div>
      </div>

      <h2 class="text-xl font-bold mb-2 text-900" style="color: #4D151B;">Are you sure you want to delete your account?
      </h2>
      <p class="text-600 mb-4 text-sm line-height-3 px-2">
        Deleting your account will permanently remove your profile, settings, and all your data.<br />
        This action cannot be undone!
      </p>

      <div v-if="roleWarning"
        class="bg-yellow-50 border-1 border-yellow-200 p-3 border-round mb-4 text-left w-full flex gap-2">
        <i class="pi pi-exclamation-triangle text-yellow-600 mt-1"></i>
        <span class="text-yellow-800 text-sm line-height-3">{{ roleWarning }}</span>
      </div>

      <div class="w-full text-left mb-5 flex flex-column gap-3 pl-4">
        <div class="flex align-items-center cursor-pointer">
          <Checkbox v-model="wipeData" inputId="wipe" :binary="true" />
          <label for="wipe" class="ml-2 text-700 text-sm cursor-pointer">Wipe out my all data on iLearn'd Tutors</label>
        </div>
        <div class="flex align-items-center cursor-pointer">
          <Checkbox v-model="sendEmail" inputId="email" :binary="true" />
          <label for="email" class="ml-2 text-700 text-sm cursor-pointer">Send me an email after deleting & wipe outing
            my all data</label>
        </div>
        <div v-if="role === 'institute'" class="flex align-items-center cursor-pointer">
          <Checkbox v-model="removeAssociatedMembers" inputId="removeMembers" :binary="true" />
          <label for="removeMembers" class="ml-2 text-700 text-sm cursor-pointer">Permanently delete all associated tutors and students linked to my institute</label>
        </div>
      </div>

      <div class="flex justify-content-center gap-3 w-full">
        <Button label="No, I changed my mind" :class="buttonClass" @click="$emit('cancel')" />
        <Button label="Yes, Delete account" :class="buttonClass"
          @click="$emit('next', { wipeData, sendEmail, removeAssociatedMembers })" />
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits, defineProps } from 'vue';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';

const props = defineProps({ role: String });
defineEmits(['next', 'cancel']);

const wipeData = ref(false);
const sendEmail = ref(false);
const removeAssociatedMembers = ref(false);

const roleWarning = computed(() => {
  switch (props.role) {
    case 'tutor': return 'All your enrolled students will lose access to your scheduled classes. Please inform your students before proceeding.';
    case 'institute': return 'All managed tutors and students under your institute will lose access. Your active subscription will also be cancelled.';
    case 'student': return 'Your enrolled classes and session history with all tutors will be permanently removed.';
    default: return null;
  }
});

const buttonClass = computed(() => {
  switch (props.role) {
    case 'student': return 'orange-button';
    case 'tutor': return 'teal-button';
    case 'institute': return 'yellow-button';
    default: return 'orange-button';
  }
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(17, 24, 39, 0.295);
  padding: 1.5rem;
  backdrop-filter: blur(3px);
}

.modal-card {
  background: white;
  width: 100%;
  max-width: 45rem;
  padding: 4rem 3rem;
  border-radius: 0.5rem;
  box-shadow: 0 4px 20px -4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.icon-box {
  width: 3.5rem;
  height: 3.5rem;
  border: 1px solid;
}

.danger-bg {
  background: #FEF2F2;
  border-color: #FECACA;
}

@media (max-width: 640px) {
  .modal-overlay {
    padding: 1rem;
  }

  .modal-card {
    padding: 2rem 1.25rem;
  }

  .flex.justify-content-center.gap-3 {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
